import { KanbanBoard, KanbanCard } from "@/ui/KanbanBoard";
import { Calendar, Plus } from "lucide-react";
import { formatDate } from "@/utils/helpers";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

import DatePicker from "@/components/ui/date-picker";
import { useState } from "react";
import { useCreatePursuitTask } from "./useCreatePursuitTask";
import TaskStatusSelect from "./TaskStatusSelect";
import TextEditor from "@/ui/TextEditor";
import { updatePursuitTask } from "@/features/pursuit/api/apiPursuitTask";
import PursuitTaskOperations from "./PursuitTaskOperations";
import {
  DropdownMenu,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

import { Ellipsis } from "lucide-react";
import TaskPrioritySelect from "./TaskPrioritySelect";
import { useUpdatePursuitTaskKanban } from "./useUpdatePursuitTaskKanban";
import { useNavigate } from "react-router-dom";

const statusColumns = [
  {
    id: "to_do",
    title: "To Do",
    columnColor: "var(--kanban-column-gray)",
    cardColor: "var(--kanban-card-gray)",
    addCardColor: "var(--gray-300)",
  },
  {
    id: "in_progress",
    title: "In Progress",
    columnColor: "var(--kanban-column-blue)",
    cardColor: "var(--kanban-card-blue)",
    addCardColor: "var(--blue-300)",
  },
  {
    id: "done",
    title: "Done",
    columnColor: "var(--kanban-column-green)",
    cardColor: "var(--kanban-card-green)",
    addCardColor: "var(--emerald-300)",
  },
];

const formSchema = z.object({
  title: z.string().trim().min(1, {
    message: "Title cannot be empty",
  }),
  description: z.any().optional(),
  status: z.string().trim(),
  priority: z.string().trim(),
  due_date: z.date().optional(),
});

export function PursuitTaskCreator({ status, pursuitId, ...props }) {
  const { mutate: createCard, isLoading } = useCreatePursuitTask(pursuitId);
  const [open, onOpenChange] = useState(false);
  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      status: status || "to_do",
      priority: "low",
    },
  });

  function onSubmit(data) {
    createCard({
      ...data,
      pursuit_id: pursuitId,
    });
    onOpenChange(false);
  }
  const color = statusColumns.find(
    (column) => column.id === status
  )?.addCardColor;
  const getColorOpacity = (opacity) =>
    color
      ? `color-mix(in srgb, var(${color}) ${opacity}%, transparent)`
      : `color-mix(in hsl, hsl(var(--muted)) ${opacity}%, transparent)`;

  return (
    <div>
      <Button
        onClick={() => onOpenChange(true)}
        style={
          status
            ? {
                backgroundColor: getColorOpacity(30),
                borderColor: getColorOpacity(100),
                color: getColorOpacity(100),
              }
            : {}
        }
        {...props}
      >
        <Plus /> New Task
      </Button>

      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogTitle />
        <DialogContent className="max-w-3xl">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              <FormField
                control={form.control}
                name="title"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <Input
                        placeholder="Task Title"
                        className="md:text-2xl font-bold border-none focus-visible:ring-0 focus-visible:ring-offset-0 placeholder:font-normal placeholder:text-muted-foreground/50"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Description</FormLabel>
                    <FormControl>
                      <TextEditor
                        className="h-[30vh] overflow-y-auto"
                        onChange={(editor) =>
                          field.onChange({ blocks: editor.document })
                        }
                        editable={!isLoading}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <div className="flex gap-4">
                <FormField
                  control={form.control}
                  name="status"
                  render={({ field }) => (
                    <FormItem className="border p-2 rounded-md flex flex-col items-center bg-[var(--gray-100)]">
                      <FormLabel>Status</FormLabel>
                      <TaskStatusSelect
                        defaultValue={field.value}
                        onValueChange={field.onChange}
                      />
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="priority"
                  render={({ field }) => (
                    <FormItem className="border p-2 rounded-md flex flex-col items-center bg-[var(--gray-100)]">
                      <FormLabel>Priority</FormLabel>
                      <TaskPrioritySelect
                        defaultValue={field.value}
                        onValueChange={field.onChange}
                      />
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="due_date"
                  render={({ field }) => (
                    <FormItem className="border p-2 rounded-md flex flex-col items-center bg-[var(--gray-100)]">
                      <FormLabel>Due Date</FormLabel>
                      <div>
                        <DatePicker
                          selected={field.value}
                          onSelect={field.onChange}
                          placeholder="Pick due date"
                          className="border-none bg-transparent w-full"
                        />
                      </div>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <Button type="submit">Create</Button>
            </form>
          </Form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function PursuitTasksKanban({ pursuit_id, data, isLoading }) {
  const { mutate } = useUpdatePursuitTaskKanban(pursuit_id);
  const navigate = useNavigate();

  return (
    <KanbanBoard
      statusColumns={statusColumns}
      updateKanban={mutate}
      renderCreateCard={(props) => (
        <PursuitTaskCreator
          pursuitId={pursuit_id}
          className="h-12 w-full border font-normal  bg-transparent text-muted-foreground hover:text-muted-foreground"
          variant="muted"
          {...props}
        />
      )} // Pass pursuitId here
      isLoading={isLoading}
    >
      {data?.map((task) => (
        <KanbanCard
          item={{ ...task, draggableId: task.id }}
          key={task.id}
          onClick={() => navigate(`./${task.id}`)}
          style={{
            backgroundColor: statusColumns.find(
              (column) => column.id === task.status
            ).cardColor,
          }}
          className="group hover:shadow-lg"
        >
          <DropdownMenu>
            <div className="relative font-medium mb-2 hover:underline cursor-pointer">
              {task.title}
              <DropdownMenuTrigger
                className="px-2 h-8 absolute top-0 right-0 group-hover:border group-hover:bg-[var(--gray-100)] hover:!bg-[var(--gray-50)] rounded-md duration-0"
                onClick={(e) => e.stopPropagation()} // Prevents onClick from bubbling
              >
                <Ellipsis className="text-muted-foreground hidden group-hover:block" />
              </DropdownMenuTrigger>
            </div>
            <PursuitTaskOperations pursuitId={pursuit_id} taskId={task.id} />
          </DropdownMenu>

          <div className="flex flex-col  items-start space-y-2 text-sm text-muted-foreground">
            {task?.due_date && (
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                <span>{formatDate(task?.due_date)}</span>
              </div>
            )}

            {task?.priority && (
              <div className="flex items-center gap-2">
                <TaskPrioritySelect
                  defaultValue={task?.priority}
                  onValueChange={(priority) =>
                    updatePursuitTask({ task_id: task.id, priority })
                  }
                  triggerClassName={"p-0"}
                />
              </div>
            )}
          </div>
        </KanbanCard>
      ))}
    </KanbanBoard>
  );
}
